import React, { useState, useEffect } from 'react'
import { Wifi, ArrowLeft, Check, Shield, Star, Loader2, CreditCard } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast, { Toaster } from 'react-hot-toast'

const PaymentPage = () => {
  const navigate = useNavigate()
  const [signupData, setSignupData] = useState<any>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const storedData = localStorage.getItem('signupData')
    if (!storedData) {
      navigate('/signup')
      return
    }
    setSignupData(JSON.parse(storedData))
  }, [navigate])

  const handleStartTrial = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      toast.loading('Creating checkout session...', { id: 'checkout' })
      
      // Create Stripe Checkout Session
      const response = await axios.post('https://flowbeatiq.com/api/billing/create-checkout', {
        email: signupData.email,
        business_name: signupData.businessName,
        password: signupData.password,  // Pass password for account creation later
        location_count: signupData.locationCount  // For multi-sensor billing
      })

      toast.success('Redirecting to secure payment...', { id: 'checkout' })
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url
      
    } catch (err: any) {
      console.error('Checkout error:', err)
      setError(err.response?.data?.detail || 'Failed to create checkout session')
      toast.error('Unable to start checkout', { id: 'checkout' })
      setIsProcessing(false)
    }
  }

  const getPrice = () => {
    const basePrice = 99
    const additionalLocations = signupData?.locationCount === '1' ? 0 : 
      signupData?.locationCount === '5+' ? 4 : parseInt(signupData?.locationCount || '1') - 1
    return basePrice + (additionalLocations * 79)
  }

  if (!signupData) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/signup')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 group"
            disabled={isProcessing}
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back
          </button>
          
          <div className="flex items-center gap-2">
            <Wifi className="w-6 h-6 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">FlowBeatIQ</span>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Business:</span>
                <span className="font-semibold">{signupData.businessName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-semibold">{signupData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Locations:</span>
                <span className="font-semibold">{signupData.locationCount}</span>
              </div>
            </div>

            <div className="border-t pt-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Starter Plan (1 sensor)</span>
                <span className="font-semibold">$99/month</span>
              </div>
              <div className="border-t pt-2 mt-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Due Today</span>
                  <span>${getPrice()}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">First month + hardware shipping</p>
              </div>
            </div>

            {/* Trust signals */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-blue-900">14-Day Risk-Free Trial</span>
              </div>
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>Hardware ships immediately</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>Cancel anytime — no long-term commitment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>Charged $99 today for first month</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Secure Checkout</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">
                      Powered by Stripe
                    </p>
                    <p className="text-sm text-blue-700">
                      You'll be securely redirected to Stripe to complete your payment. 
                      Your credit card information is never stored on our servers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Secure 256-bit SSL encryption</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>PCI DSS compliant payment processing</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>14-day risk-free period</span>
                </div>
              </div>
            </div>

            <div>
              <button
                onClick={handleStartTrial}
                disabled={isProcessing}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating secure checkout...
                  </>
                ) : (
                  <>
                    Continue to Secure Payment
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500 mt-4">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>

        {/* Social Proof */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
            ))}
            <span className="ml-2 text-gray-600">Trusted by 100+ businesses</span>
          </div>
          <p className="text-gray-500">
            "The best investment we've made for understanding our foot traffic" - Sarah M.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage
